import type {
  FinderLocationInput,
  FinderPreference,
  FinderPreferenceInput,
  TimeOfDay,
  WeekDay,
} from './types'

export interface FinderForm {
  sportId: string
  /** Specific basic-stats (generic) sport; '' for full-stats sports. */
  genericSportId: string
  sportName: string
  positionId: string
  positionName: string
  skillLevel: string | null
  days: WeekDay[]
  times: TimeOfDay[]
  bio: string
  /** Per-game charge rate as typed (string for the input); '' when unset. */
  rate: string
  /** SN-8295 — the card's location: country / province ids ('' when unset). */
  countryId: string
  provinceId: string
  city: string
}

/** SN-8295 — the card's location, as the form holds it. */
export type FormLocation = Pick<FinderForm, 'countryId' | 'provinceId' | 'city'>

export const NO_LOCATION: FormLocation = {
  countryId: '',
  provinceId: '',
  city: '',
}

/** The location's country and province names, for pickers that show names. */
export interface LocationNames {
  countryName: string
  provinceName: string
}

/** Where the form's location starts, with the names its pickers show. */
export interface LocationSeed {
  location: FormLocation
  names: LocationNames
}

const NO_SEED: LocationSeed = {
  location: NO_LOCATION,
  names: { countryName: '', provinceName: '' },
}

interface Place {
  id?: number | string | null
  name?: string | null
}

/** The player's profile address (GET /users/me/players/:id), as far as an
 *  Add's location reads it. */
export interface SeedAddress {
  city?: string | null
  country?: Place | null
  province?: Place | null
}

/**
 * SN-8295 — an Add starts from the player's profile address, but only a
 * complete one: country, province and a city, since the API takes a card's
 * location only whole. Anything less starts blank, names included. It is only
 * read: the card never writes it back.
 */
export const addSeed = (address?: SeedAddress | null): LocationSeed => {
  const city = address?.city?.trim() ?? ''
  const countryId = address?.country?.id
  const provinceId = address?.province?.id
  if (countryId == null || provinceId == null || city === '') {
    return NO_SEED
  }
  return {
    location: {
      countryId: String(countryId),
      provinceId: String(provinceId),
      city,
    },
    names: {
      countryName: address?.country?.name ?? '',
      provinceName: address?.province?.name ?? '',
    },
  }
}

/** SN-8295 — an Edit starts from the card's own location, blank without one. */
export const editSeed = (preference: FinderPreference): LocationSeed => {
  const { location } = preference
  if (!location) {
    return NO_SEED
  }
  return {
    location: {
      countryId: location.country ? String(location.country.id) : '',
      provinceId: location.province ? String(location.province.id) : '',
      city: location.city ?? '',
    },
    names: {
      countryName: location.country?.name ?? '',
      provinceName: location.province?.name ?? '',
    },
  }
}

export const emptyForm = (): FinderForm => ({
  sportId: '',
  genericSportId: '',
  sportName: '',
  positionId: '',
  positionName: '',
  skillLevel: null,
  days: [],
  times: [],
  bio: '',
  rate: '',
  ...NO_LOCATION,
})

export const formFromPreference = (p: FinderPreference): FinderForm => ({
  sportId: p.sport_id,
  genericSportId: p.generic_sport_id,
  sportName: p.sport_name,
  positionId: p.preferred_position,
  positionName: p.position_name,
  skillLevel: p.skill_level,
  days: [...p.available_days],
  times: [...(p.available_times ?? [])],
  bio: p.bio ?? '',
  rate: p.charge_rate != null ? String(p.charge_rate) : '',
  ...editSeed(p).location,
})

/** A picked id as the API takes it; none ('') is sent as null, never NaN. The
 *  forms keep typed State/Province text out of provinceId, so only ids get here. */
const toId = (value: string): number | null =>
  /^\d+$/.test(value) ? Number(value) : null

const isSameLocation = (a: FormLocation, b: FormLocation): boolean =>
  a.countryId === b.countryId &&
  a.provinceId === b.provinceId &&
  a.city.trim() === b.city.trim()

/**
 * SN-8295 — the card's location sent with a preference: only a change to the
 * location the form started from (`seed`). An Edit starts from the card's, so
 * an untouched one is never re-sent; an Add starts from none, so a location
 * pre-filled from the profile address is sent as the card's. Left blank it is
 * not sent (null keeps the card's location on an edit and saves none on an
 * add); partly filled it is sent as is, and the API names each missing field.
 */
export const locationToInput = (
  form: FormLocation,
  seed: FormLocation = NO_LOCATION
): FinderLocationInput | null => {
  const city = form.city.trim()
  if (
    isSameLocation(form, seed) ||
    (!form.countryId && !form.provinceId && city === '')
  ) {
    return null
  }
  return {
    city,
    province_id: toId(form.provinceId),
    country_id: toId(form.countryId),
  }
}

export const toggleDay = (form: FinderForm, day: WeekDay): FinderForm => {
  const has = form.days.includes(day)
  return {
    ...form,
    days: has ? form.days.filter((d) => d !== day) : [...form.days, day],
  }
}

export const toggleTime = (form: FinderForm, time: TimeOfDay): FinderForm => {
  const has = form.times.includes(time)
  return {
    ...form,
    times: has ? form.times.filter((t) => t !== time) : [...form.times, time],
  }
}

export const isFormValid = (form: FinderForm): boolean =>
  form.sportId.trim().length > 0 && form.positionId.trim().length > 0

/**
 * Build the add/update request body from form state. `gender_id` isn't a form
 * field — it comes from the player's profile; `is_public` defaults to true (a
 * new listing is a self opt-in, public by default). `seedLocation` is the
 * location the form started from — the card's on an Edit (`editSeed`), none on
 * an Add — so only a change is sent.
 */
export const formToInput = (
  form: FinderForm,
  opts: {
    genderId: number | null
    isPublic?: boolean
    seedLocation?: FormLocation
  }
): FinderPreferenceInput => ({
  // Sport ids are UUID strings (unlike numeric player_type_id) — send as-is;
  // coercing with Number() yields NaN → serializes to null.
  sport_id: form.sportId || null,
  generic_sport_id: form.genericSportId || null,
  player_type_id: form.positionId ? Number(form.positionId) : null,
  skill_level: form.skillLevel,
  bio: form.bio.trim().length > 0 ? form.bio.trim() : null,
  // Rate is optional and never client-validated (the API validates); send the
  // parsed number, or null when blank / not a valid number.
  charge_rate:
    form.rate.trim().length > 0 && !Number.isNaN(Number(form.rate))
      ? Number(form.rate)
      : null,
  available_days: form.days,
  available_times: form.times,
  gender_id: opts.genderId,
  is_public: opts.isPublic ?? true,
  location: locationToInput(form, opts.seedLocation),
})
